/**
 * G1/G2/G3 — after OIDC / auth hydrates a `customerId`, run one parked post-pay
 * claim (link-account or refund). Clear pending + record outcome ONLY on success.
 * Skip mutate when succeeded outcome already exists; own in-flight claim token.
 * Stale settle after TTL: ignore via claim generation (G2).
 */

import { useEffect, useRef, useState } from 'react';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { useLinkAccountToTransaction } from '../auth/authApi.js';
import { useSubmitCustomerRefundRequest } from '../checkout/checkoutApi.js';
import { linkAccountIdempotencyKey } from './linkAccountHelpers.js';
import {
  beginPostPayAuthClaimInFlight,
  clearPendingPostPayAuthAction,
  endPostPayAuthClaimInFlight,
  hasSucceededPostPayAuthOutcome,
  isPostPayAuthClaimGenerationCurrent,
  peekPendingPostPayAuthAction,
  POST_PAY_AUTH_CLAIM_TTL_MS,
  recordPostPayAuthActionOutcome,
  type PostPayPendingAuthAction,
} from './postPayPendingAuthAction.js';

/** Max mutation attempts per hydrated session before stopping (no infinite loop). */
export const POST_PAY_AUTH_ACTION_MAX_ATTEMPTS = 3;

/** Delay before resetting ranRef so the effect can retry after a failed claim. */
export const POST_PAY_AUTH_ACTION_RETRY_BACKOFF_MS = 1_000;

export function usePostPayPendingAuthActionRunner(): void {
  const { session, sessionHydrated } = useCustomerSession();
  const { mutate: mutateLinkAccount } = useLinkAccountToTransaction();
  const { mutate: mutateSubmitRefund } = useSubmitCustomerRefundRequest();
  const ranRef = useRef(false);
  const attemptRef = useRef(0);
  const backoffTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [retryTick, setRetryTick] = useState(0);
  const customerId = session?.customerId;

  useEffect(() => {
    const releaseBackoffForReentry = (): void => {
      if (backoffTimerRef.current !== null) {
        clearTimeout(backoffTimerRef.current);
        backoffTimerRef.current = null;
        ranRef.current = false;
      }
    };

    if (!sessionHydrated || customerId === undefined) {
      ranRef.current = false;
      attemptRef.current = 0;
      if (backoffTimerRef.current !== null) {
        clearTimeout(backoffTimerRef.current);
        backoffTimerRef.current = null;
      }
      return;
    }
    if (ranRef.current) {
      return releaseBackoffForReentry;
    }

    const pending = peekPendingPostPayAuthAction();
    if (pending === null) {
      return;
    }

    // Leftover pending after success: clear and no-op (no second POST).
    if (
      hasSucceededPostPayAuthOutcome({
        kind: pending.kind,
        paymentId: pending.paymentId,
      })
    ) {
      clearPendingPostPayAuthAction();
      attemptRef.current = 0;
      return;
    }

    if (attemptRef.current >= POST_PAY_AUTH_ACTION_MAX_ATTEMPTS) {
      return;
    }

    // Begin failure must not set ranRef — leave slot free for CTA/OTP owner.
    const generation = beginPostPayAuthClaimInFlight({
      kind: pending.kind,
      paymentId: pending.paymentId,
    });
    if (generation === null) {
      // CTA/OTP already owns this claim — do not parallel mutate.
      return;
    }

    // Owned by this effect run only after successful begin (cleanup can free safely).
    let claimOwned = true;
    /** Set when TTL/cleanup abandons this attempt — stale mutate callbacks must no-op. */
    let claimAbandoned = false;
    ranRef.current = true;
    attemptRef.current += 1;

    const abandonOwnedClaim = (): void => {
      if (!claimOwned) {
        return;
      }
      claimAbandoned = true;
      endPostPayAuthClaimInFlight({
        kind: pending.kind,
        paymentId: pending.paymentId,
      });
      claimOwned = false;
      ranRef.current = false;
    };

    // Mirror module claim TTL so hung mutate also resets ranRef (not only the slot).
    let claimTtlTimer: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      claimTtlTimer = null;
      abandonOwnedClaim();
      setRetryTick((tick) => tick + 1);
    }, POST_PAY_AUTH_CLAIM_TTL_MS);

    const clearClaimTtlTimer = (): void => {
      if (claimTtlTimer !== null) {
        clearTimeout(claimTtlTimer);
        claimTtlTimer = null;
      }
    };

    const isThisClaimCurrent = (): boolean =>
      !claimAbandoned &&
      isPostPayAuthClaimGenerationCurrent({
        kind: pending.kind,
        paymentId: pending.paymentId,
        generation,
      });

    const releaseClaim = (action: PostPayPendingAuthAction): void => {
      clearClaimTtlTimer();
      if (
        !claimAbandoned &&
        isPostPayAuthClaimGenerationCurrent({
          kind: action.kind,
          paymentId: action.paymentId,
          generation,
        })
      ) {
        endPostPayAuthClaimInFlight({
          kind: action.kind,
          paymentId: action.paymentId,
        });
      }
    };

    const onMutationSuccess = (action: PostPayPendingAuthAction): void => {
      // G2 — stale settle after TTL/retry: do not clear pending / record / end.
      if (!isThisClaimCurrent()) {
        return;
      }
      if (backoffTimerRef.current !== null) {
        clearTimeout(backoffTimerRef.current);
        backoffTimerRef.current = null;
      }
      clearPendingPostPayAuthAction();
      recordPostPayAuthActionOutcome({
        kind: action.kind,
        paymentId: action.paymentId,
      });
      releaseClaim(action);
      claimOwned = false;
      attemptRef.current = 0;
      ranRef.current = false;
    };

    const onMutationError = (action: PostPayPendingAuthAction): void => {
      if (!isThisClaimCurrent()) {
        return;
      }
      releaseClaim(action);
      claimOwned = false;
      if (attemptRef.current >= POST_PAY_AUTH_ACTION_MAX_ATTEMPTS) {
        return;
      }
      if (backoffTimerRef.current !== null) {
        clearTimeout(backoffTimerRef.current);
        backoffTimerRef.current = null;
      }
      backoffTimerRef.current = setTimeout(() => {
        backoffTimerRef.current = null;
        ranRef.current = false;
        setRetryTick((tick) => tick + 1);
      }, POST_PAY_AUTH_ACTION_RETRY_BACKOFF_MS);
    };

    if (pending.kind === 'link-account') {
      mutateLinkAccount(
        {
          paymentId: pending.paymentId,
          customerId,
          idempotencyKey: linkAccountIdempotencyKey(pending.paymentId, customerId),
          tenantCode: pending.tenantCode,
        },
        {
          onSuccess: () => {
            onMutationSuccess(pending);
          },
          onError: () => {
            onMutationError(pending);
          },
        },
      );
    } else {
      const refundTenantOverride =
        pending.apiTenantCode !== undefined && pending.apiTenantCode.trim().length > 0
          ? pending.apiTenantCode.trim()
          : pending.tenantCode;

      mutateSubmitRefund(
        {
          paymentId: pending.paymentId,
          tenantCode: refundTenantOverride,
        },
        {
          onSuccess: () => {
            onMutationSuccess(pending);
          },
          onError: () => {
            onMutationError(pending);
          },
        },
      );
    }

    // Hung mutate while still mounted: module claim TTL + claimTtlTimer above free the
    // slot and ranRef. Stale callbacks no-op via generation. Cleanup ends owned claim.
    return (): void => {
      clearClaimTtlTimer();
      releaseBackoffForReentry();
      if (claimOwned) {
        abandonOwnedClaim();
      }
    };
  }, [
    customerId,
    mutateLinkAccount,
    mutateSubmitRefund,
    retryTick,
    sessionHydrated,
  ]);
}
